import { useState } from 'react';
import {
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { CheckIcon } from '@heroicons/react/24/solid';
import { Button, Card, CardHeader, ContentLoader, Modal, ModalFooter } from '@/components/common';
import {
  usePOSP,
  useApprovePOSP,
  useRejectPOSP,
  useActivatePOSP,
  useCreatePOSPLogin,
  usePospChannel,
} from '@/hooks/useBrokers';
import { useSubmitChannelDocumentsForReview } from '@/hooks/useKycDocuments';
import { useAuthStore } from '@/store/auth';
import type { POSPStatus, POSPLoginCredentials } from '@/types';
import { ACTIVATION_STEPS, getStepIndex } from './pospOnboarding';
import { ChannelKycDocumentsPanel } from './ChannelKycDocumentsPanel';

// ── Status Timeline ──────────────────────────────────────────────────

function StatusTimeline({ status, rejectionReason }: { status: POSPStatus; rejectionReason: string | null }) {
  const isRejected = status === 'rejected';
  const currentIdx = isRejected ? getStepIndex('under_review') : getStepIndex(status);

  return (
    <Card>
      <CardHeader title="Activation Progress" />
      {isRejected && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
          <XCircleIcon className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-red-800">Application Rejected</p>
            {rejectionReason && (
              <p className="text-sm text-red-600 mt-1">{rejectionReason}</p>
            )}
          </div>
        </div>
      )}
      <div className="flex items-center justify-between">
        {ACTIVATION_STEPS.map((step, idx) => {
          const isCompleted = idx < currentIdx;
          const isCurrent = idx === currentIdx && !isRejected;

          return (
            <div key={step.key} className="flex items-center flex-1 last:flex-initial">
              <div className="flex flex-col items-center">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    isCompleted
                      ? 'bg-green-500 text-white'
                      : isCurrent
                        ? 'bg-primary-600 text-white ring-4 ring-primary-100'
                        : isRejected && idx === currentIdx
                          ? 'bg-red-500 text-white ring-4 ring-red-100'
                          : 'bg-gray-200 text-gray-500'
                  }`}
                >
                  {isCompleted ? (
                    <CheckIcon className="w-4 h-4" />
                  ) : isRejected && idx === currentIdx ? (
                    <XCircleIcon className="w-4 h-4" />
                  ) : (
                    idx + 1
                  )}
                </div>
                <span
                  className={`mt-2 text-xs text-center whitespace-nowrap ${
                    isCompleted
                      ? 'text-green-600 font-medium'
                      : isCurrent
                        ? 'text-primary-600 font-medium'
                        : isRejected && idx === currentIdx
                          ? 'text-red-600 font-medium'
                          : 'text-gray-400'
                  }`}
                >
                  {step.label}
                </span>
              </div>
              {idx < ACTIVATION_STEPS.length - 1 && (
                <div
                  className={`flex-1 h-0.5 mx-2 mt-[-1rem] ${
                    idx < currentIdx ? 'bg-green-500' : 'bg-gray-200'
                  }`}
                />
              )}
            </div>
          );
        })}
      </div>
    </Card>
  );
}

// ── Required Documents ───────────────────────────────────────────────

/**
 * The agent's own KYC documents, and the button that sends them for review.
 *
 * What was here before listed the four required documents, offered an Upload
 * button, gathered the files into a FormData and POSTed it to
 * `/posps/{id}/submit-documents/` — an endpoint that read `request.FILES` for
 * `.size` and discarded the bytes. The toast said "Documents submitted
 * successfully", the stepper advanced, and nothing had been stored anywhere.
 * `POSPDocument.file_url` was a URLField that no upload path ever wrote.
 *
 * Files now go one at a time to `/brokers/channel-documents/`, keyed on the
 * channel that represents this agent, so they land in the SAME rows the Broker
 * Admin sees on that channel's detail page — which is the other half of the
 * requirement. `submit-documents` keeps only the job it can actually do: move
 * the application along, and refuse when there is nothing behind it.
 *
 * Exported because decision 16 puts it LAST in the agent's Profile tab, with
 * certifications, bank details and broker mapping between it and the stepper it
 * used to sit against. One component either way, mounted by whoever arranges
 * the order.
 */
export function PospRequiredDocumentsSection({
  pospId,
  pospStatus,
}: {
  pospId: string;
  pospStatus: POSPStatus;
}) {
  const { data: channel, isLoading, isError, refetch } = usePospChannel(pospId);
  const submitMutation = useSubmitChannelDocumentsForReview(pospId);
  const channelId = channel?.channel_id ?? null;

  // Loading, error and "there is no channel" are three different answers and
  // must not be collapsed into one empty panel.
  if (isLoading) {
    return (
      <Card>
        <CardHeader title="Required Documents" />
        <p className="text-sm text-gray-500">Loading your documents…</p>
      </Card>
    );
  }

  if (isError) {
    return (
      <Card>
        <CardHeader title="Required Documents" />
        <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3">
          <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-500" />
          <div>
            <p className="text-sm font-medium text-red-800">
              We could not load your documents — this is not the same as there being none.
            </p>
            <Button size="sm" variant="outline" className="mt-2" onClick={() => refetch()}>
              Try again
            </Button>
          </div>
        </div>
      </Card>
    );
  }

  if (!channelId) {
    // An agent with no owning channel cannot store documents anywhere. Saying
    // so beats an upload button that would 400 on every press.
    return (
      <Card>
        <CardHeader title="Required Documents" />
        <p className="text-sm text-gray-500">
          Your profile has no channel record yet, so documents cannot be uploaded. Ask your broker to
          set one up.
        </p>
      </Card>
    );
  }

  // The stepper only has somewhere to go from these two states; in the others
  // the button would be a no-op dressed as an action.
  const canSubmitForReview = pospStatus === 'pending' || pospStatus === 'rejected';

  return (
    <div className="space-y-3">
      <ChannelKycDocumentsPanel
        channelId={channelId}
        canVerify={false}
        title="Required Documents"
        subtitle="Upload your documents here. Your broker reviews each one and their status and remarks appear below."
      />
      {canSubmitForReview && (
        <Button
          variant="primary"
          disabled={submitMutation.isPending}
          onClick={() => submitMutation.mutate()}
        >
          {submitMutation.isPending ? 'Submitting…' : 'Submit for review'}
        </Button>
      )}
    </div>
  );
}

// ── Reject Modal ─────────────────────────────────────────────────────

function RejectModal({
  isOpen,
  onClose,
  onConfirm,
  isLoading,
}: {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => void;
  isLoading: boolean;
}) {
  const [reason, setReason] = useState('');

  const handleSubmit = () => {
    if (reason.trim()) {
      onConfirm(reason.trim());
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Reject POSP Application" size="sm">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Rejection Reason
        </label>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          placeholder="Provide a reason for rejection..."
        />
      </div>
      <ModalFooter>
        <Button variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="danger"
          onClick={handleSubmit}
          disabled={!reason.trim() || isLoading}
        >
          {isLoading ? 'Rejecting...' : 'Reject'}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

// ── Login Credentials Modal (shown once) ─────────────────────────────

function PospLoginCredentialsModal({
  credentials,
  onClose,
}: {
  credentials: POSPLoginCredentials | null;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState<string | null>(null);

  const handleCopy = async (label: string, value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(label);
      setTimeout(() => setCopied((c) => (c === label ? null : c)), 2000);
    } catch {
      setCopied(null);
    }
  };

  return (
    <Modal isOpen={!!credentials} onClose={onClose} title="POSP Login Credentials" size="md">
      {credentials && (
        <div className="space-y-4">
          <div className="rounded-md bg-amber-50 border border-amber-200 p-3 dark:bg-amber-900/20 dark:border-amber-800">
            <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
              This password is shown only once.
            </p>
            <p className="mt-1 text-xs text-amber-700 dark:text-amber-400">{credentials.message}</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Login (email / phone)
            </label>
            <input
              type="text"
              readOnly
              value={credentials.email || credentials.phone || '—'}
              className="mt-1 block w-full rounded-md border-gray-300 bg-gray-50 shadow-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white sm:text-sm"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Temporary Password
            </label>
            <div className="mt-1 flex gap-2">
              <input
                type="text"
                readOnly
                value={credentials.password}
                className="block w-full rounded-md border-gray-300 bg-gray-50 font-mono shadow-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white sm:text-sm"
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => handleCopy('password', credentials.password)}
              >
                {copied === 'password' ? 'Copied' : 'Copy'}
              </Button>
            </div>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              The POSP must change this password on first login.
            </p>
          </div>
        </div>
      )}
      <ModalFooter>
        <Button variant="primary" onClick={onClose}>
          Done
        </Button>
      </ModalFooter>
    </Modal>
  );
}

// ── Login Management (broker_admin / country_admin / super_admin only) ──

/**
 * Issue the temp password a POSP signs in with, shown once.
 *
 * M-05: exported so the broker admin's POSP profile (ChannelDetailPage) can
 * mount it, not just the POSP's own profile. `passwordSet` comes from the
 * unified channel row: `false` means the agent's User has no usable password
 * yet ("Create login"), `true` means this is a reset ("Issue a new password"),
 * `undefined` means the caller does not know (the legacy wording).
 *
 * A reset is confirmed first, because it is not only a new password: the
 * server also blacklists every refresh token the old one minted, so every
 * session ends once its access token expires (within the hour). That is the point of the button (lost phone, agent
 * left) -- but it should never happen from a stray click.
 */
export function PospLoginSection({
  pospId,
  passwordSet,
}: {
  pospId: string;
  passwordSet?: boolean;
}) {
  const createLoginMutation = useCreatePOSPLogin();
  const [credentials, setCredentials] = useState<POSPLoginCredentials | null>(null);
  const [confirmReset, setConfirmReset] = useState(false);
  const [sessionsEnded, setSessionsEnded] = useState<number | null>(null);

  const label =
    passwordSet === false
      ? 'Create login'
      : passwordSet === true
        ? 'Issue a new password'
        : 'Create / Reset Login';

  const issue = () => {
    setConfirmReset(false);
    createLoginMutation.mutate(pospId, {
      onSuccess: (data) => {
        setCredentials(data.credentials);
        setSessionsEnded(data.reset ? (data.sessions_ended ?? 0) : null);
      },
    });
  };

  const handleClick = () => {
    if (passwordSet === true) setConfirmReset(true);
    else issue();
  };

  return (
    <Card>
      <CardHeader
        title="POSP Login"
        subtitle={
          passwordSet === false
            ? 'This agent cannot sign in yet. Create a login to issue their first temporary password.'
            : 'Issue or reset the temporary password this POSP uses to sign in.'
        }
      />
      <Button
        variant="outline"
        onClick={handleClick}
        disabled={createLoginMutation.isPending}
      >
        {createLoginMutation.isPending ? 'Generating...' : label}
      </Button>
      {sessionsEnded !== null && (
        <p className="mt-3 text-sm text-gray-600 dark:text-gray-300">
          {sessionsEnded === 0
            ? 'The old password no longer works. The agent had no other signed-in sessions.'
            : `The old password no longer works, and ${sessionsEnded} signed-in session${
                sessionsEnded !== 1 ? 's were' : ' was'
              } ended.`}
        </p>
      )}
      <Modal
        isOpen={confirmReset}
        onClose={() => setConfirmReset(false)}
        title="Issue a new password?"
        size="md"
      >
        <p className="text-sm text-gray-700 dark:text-gray-200">
          The agent&rsquo;s current password stops working immediately. Their saved sign-ins are
          cancelled now, so every session ends within the hour (a page already open can keep
          working until then). You will see the new temporary password once; they must change it
          when they next sign in.
        </p>
        <ModalFooter>
          <Button variant="outline" onClick={() => setConfirmReset(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={issue}>
            Issue a new password
          </Button>
        </ModalFooter>
      </Modal>
      <PospLoginCredentialsModal credentials={credentials} onClose={() => setCredentials(null)} />
    </Card>
  );
}

// ── Onboarding Panel ─────────────────────────────────────────────────

export function PospOnboardingPanel({
  pospId,
  includeDocuments = true,
}: {
  pospId: string;
  /** Decision 16: the agent's Profile tab renders Required Documents itself,
   *  last, so it asks this panel for the stepper and the review controls only.
   *  Every other caller wants the whole panel. */
  includeDocuments?: boolean;
}) {
  const { data: posp, isLoading, error } = usePOSP(pospId);
  const user = useAuthStore((s) => s.user);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const activateMutation = useActivatePOSP();
  const approveMutation = useApprovePOSP();
  const rejectMutation = useRejectPOSP();
  const canManagePOSP =
    user?.role === 'super_admin' || user?.role === 'broker_admin' || user?.role === 'country_admin';

  if (isLoading) return <ContentLoader />;
  if (error || !posp) return <p className="text-sm text-gray-500">POSP profile not found.</p>;

  return (
    <div className="space-y-6">
      <StatusTimeline status={posp.status} rejectionReason={posp.rejection_reason} />
      {canManagePOSP && <PospLoginSection pospId={posp.id} />}
      {user?.role === 'broker_admin' && posp.status === 'under_review' && (
        <Card>
          <CardHeader title="Review Application" />
          <p className="text-sm text-gray-600 mb-4">
            This POSP has submitted documents for review. Please verify the documents and approve or reject the application.
          </p>
          <div className="flex gap-2">
            <Button
              variant="primary"
              onClick={() => approveMutation.mutate(posp.id)}
              disabled={approveMutation.isPending}
              leftIcon={<CheckCircleIcon className="w-5 h-5" />}
            >
              {approveMutation.isPending ? 'Approving...' : 'Approve'}
            </Button>
            <Button
              variant="danger"
              onClick={() => setShowRejectModal(true)}
              leftIcon={<XCircleIcon className="w-5 h-5" />}
            >
              Reject
            </Button>
          </div>
        </Card>
      )}
      {includeDocuments && (
        <PospRequiredDocumentsSection pospId={posp.id} pospStatus={posp.status} />
      )}
      {canManagePOSP && posp.status !== 'active' && (
        <Button
          variant="primary"
          onClick={() => activateMutation.mutate(posp.id)}
          disabled={activateMutation.isPending}
          leftIcon={<CheckCircleIcon className="w-5 h-5" />}
        >
          {activateMutation.isPending ? 'Activating...' : 'Activate POSP'}
        </Button>
      )}
      <RejectModal
        isOpen={showRejectModal}
        onClose={() => setShowRejectModal(false)}
        onConfirm={(reason) =>
          rejectMutation.mutate({ id: posp.id, reason }, { onSuccess: () => setShowRejectModal(false) })
        }
        isLoading={rejectMutation.isPending}
      />
    </div>
  );
}
